/**
 * PressableCard — PWA port of client-native/src/ui/molecules/PressableCard.tsx
 * (source of truth). Wraps Card with a press-scale animation and a selected state.
 *
 * iOS parity notes:
 *   - Press animation: scale 0.97 on tap (RN spring → framer-motion whileTap).
 *   - Selected: 2px solid --color-light-blue (#b6d9f2) border +
 *     --color-light-blue-translucent (#F2F8FF) background. NOTE the old PWA
 *     cardStyles used #0c92f1 for this border — that was WRONG; iOS uses lightBlue.
 *   - Default padding 16, variant "border".
 */
import type { CSSProperties, ReactNode } from "react";
import { motion } from "framer-motion";
import Card, { type CardProps } from "./Card";

const DEFAULT_SELECTION_BORDER = 2;

export interface PressableCardProps {
  children: ReactNode;
  variant?: CardProps["variant"];
  /** Inner padding in px (matches iOS `padding`, default 16). */
  padding?: number;
  selected?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  /** Width of the selection border in px. Set to 0 to disable it entirely. */
  borderWidth?: number;
  /**
   * Element used for the pressable wrapper. Use "div" when the card content
   * contains interactive elements (nested <button> is invalid HTML).
   */
  as?: "button" | "div";
  /** Styles for the pressable wrapper (layout sizing: flex, min/max width…). */
  containerStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
}

const PressableCard: React.FC<PressableCardProps> = ({
  children,
  variant = "border",
  padding = 16,
  selected = false,
  disabled = false,
  onPress,
  borderWidth = DEFAULT_SELECTION_BORDER,
  as = "button",
  containerStyle,
  className,
  style,
}) => {
  const selectionStyle: CSSProperties = selected
    ? {
        border: `${borderWidth}px solid var(--color-light-blue)`,
        backgroundColor: "var(--color-light-blue-translucent)",
      }
    : {
        // Keep layout stable whether or not selected (border reserves the box).
        border: borderWidth ? `${borderWidth}px solid transparent` : undefined,
      };

  const wrapperStyle: CSSProperties = {
    display: "block",
    width: "100%",
    padding: 0,
    margin: 0,
    border: "none",
    background: "none",
    textAlign: "inherit",
    font: "inherit",
    color: "inherit",
    cursor: disabled ? "default" : "pointer",
    WebkitTapHighlightColor: "transparent",
    opacity: disabled ? 0.55 : 1,
    ...containerStyle,
  };

  const card = (
    <Card
      variant={variant}
      padding={padding}
      className={className}
      style={{ ...selectionStyle, ...style }}
    >
      {children}
    </Card>
  );

  if (as === "div") {
    return (
      <motion.div
        role="button"
        onClick={disabled ? undefined : onPress}
        whileTap={disabled ? undefined : { scale: 0.97 }}
        transition={{ type: "spring", stiffness: 600, damping: 30 }}
        style={wrapperStyle}
      >
        {card}
      </motion.div>
    );
  }

  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onPress}
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ type: "spring", stiffness: 600, damping: 30 }}
      style={wrapperStyle}
    >
      {card}
    </motion.button>
  );
};

export default PressableCard;
